import React from 'react';
import {observer} from 'mobx-react';
import * as util from '../util';

declare const modalCloseById: (id: string) => void;

interface IModalSimpleElementProps {
    id: string;
    title: string;
    open?: boolean;
    maxWidth?: string;
    children: JSX.Element;
    abTestClass?: string;
    closeButtonText?: string;
    closeButtonClass?: string;
    closeButtonDisabled?: boolean;
    close?: () => void;
    saveAndClose?: () => void;
}

@observer
export default class ModalSimple extends React.Component<IModalSimpleElementProps> {
    private modalRef = React.createRef<HTMLDivElement>();
    private observer: MutationObserver | null = null;
    private triggeredFocusElement: HTMLElement;

    constructor(props) {
        super(props);
    }

    componentDidMount(): void {
        this.subscribeToModalState();

        if (this.props.open) {
            this.show();
        }
    }

    subscribeToModalState() {
        if (this.modalRef.current) {
            this.observer = new MutationObserver(mutations => {
                mutations.forEach(mutation => {
                    const target = mutation.target as HTMLElement;

                    this.triggeredFocusElement = document.activeElement as HTMLElement;

                    if (getComputedStyle(target).display !== 'none') {
                        target.focus();
                    }
                });
            });

            this.observer.observe(this.modalRef.current, {
                attributes: true,
                attributeFilter: ['style'],
            });
        }
    }

    componentWillUnmount() {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
    }

    show() {
        util.modal(this.props.id);
    }

    hide(saveButton: boolean = false) {
        const timeout = this.props.close || this.props.saveAndClose ? 500 : 0;

        setTimeout(() => {
            modalCloseById(this.props.id);

            if (this.props.close) {
                this.props.close();
            }

            if (saveButton && this.props.saveAndClose) {
                this.props.saveAndClose();
            }
        }, timeout);
    }

    onOutsideClick(e: React.MouseEvent<HTMLDivElement>) {
        if (!e.target) {
            return;
        }

        if ((e.target as HTMLElement).classList.contains('modal')) {
            this.hide();
        }
    }

    onKeyDown(e: React.KeyboardEvent<HTMLButtonElement>, saveButton: boolean = false) {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.hide(saveButton);

            if (this.triggeredFocusElement) {
                this.triggeredFocusElement.focus();
            }
        }
    }

    render() {
        return (
            <div
                className={`modal ${this.props.abTestClass || ''}`}
                id={this.props.id}
                tabIndex={-1}
                ref={this.modalRef}
                onClick={e => this.onOutsideClick(e)}
            >
                <div
                    className="modal-dialog"
                    style={{
                        maxWidth: this.props.maxWidth ? `${this.props.maxWidth}` : '700px',
                        maxHeight: window.innerHeight * 0.9,
                    }}
                >
                    <div className="modal-content">
                        <div
                            className="modal-header"
                            role="heading"
                            aria-level={1}
                        >
                            {this.props.title}
                            <button
                                type="button"
                                className="btn-close"
                                aria-label="Close"
                                onClick={() => this.hide()}
                                onKeyDown={e => this.onKeyDown(e)}
                            ></button>
                        </div>
                        <div className="modal-body">{this.props.children}</div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className={`btn btn-primary ${this.props.closeButtonClass}`}
                                disabled={this.props.closeButtonDisabled}
                                onClick={() => this.hide(true)}
                                onKeyDown={e => this.onKeyDown(e, true)}
                            >
                                {this.props.closeButtonText || 'Close'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
